import { useEffect, useState } from "react";
import { api, brl, STATUS_LABEL, STATUS_COLOR } from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = ["draft", "sent", "viewed", "approved", "rejected", "expired"];

export default function Proposals() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/proposals").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const onDrop = async (status, pid) => {
    await api.patch(`/proposals/${pid}/status`, { status });
    toast.success(`Movido para ${STATUS_LABEL[status]}`);
    load();
  };

  const remove = async (pid) => {
    await api.delete(`/proposals/${pid}`);
    toast.success("Proposta excluída");
    load();
  };

  const groups = Object.fromEntries(COLUMNS.map((c) => [c, items.filter((i) => i.status === c)]));

  return (
    <div className="p-10 max-w-[1600px]">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="overline">Gestão de Propostas</p>
          <h1 className="font-heading text-4xl mt-2">Kanban de Orçamentos</h1>
        </div>
        <Button asChild className="bg-[#C07C88] hover:bg-[#A86571] rounded-md" data-testid="new-proposal-btn">
          <Link to="/proposals/new"><Plus size={16} className="mr-1" /> Nova Proposta</Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        {COLUMNS.map((col) => (
          <div
            key={col}
            className="rounded-lg bg-[#FAFAFA] border border-border p-3 min-h-[400px]"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              const pid = e.dataTransfer.getData("text/plain");
              if (pid) onDrop(col, pid);
            }}
            data-testid={`kanban-col-${col}`}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className="status-dot" style={{ background: STATUS_COLOR[col] }} />
                <p className="text-xs font-medium uppercase tracking-wider">{STATUS_LABEL[col]}</p>
              </div>
              <span className="text-xs text-muted-foreground">{groups[col].length}</span>
            </div>
            <div className="space-y-2">
              {groups[col].map((p) => (
                <div key={p.proposal_id} className="relative group">
                  <Link
                    to={`/proposals/${p.proposal_id}`}
                    draggable
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", p.proposal_id)}
                    data-testid={`kanban-card-${p.proposal_id}`}
                    className="block"
                  >
                    <Card className="p-3 hover:-translate-y-0.5 hover:shadow-sm transition-all cursor-grab border-border shadow-none bg-white">
                      <p className="font-mono text-[10px] text-muted-foreground">{p.number}</p>
                      <p className="font-medium text-sm mt-1 truncate pr-6">{p.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{p.client_name}</p>
                      <p className="font-heading text-base mt-2 text-[#D4AF37]">{brl(p.totals.total)}</p>
                    </Card>
                  </Link>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        className="absolute top-2 right-2 p-1 rounded text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-red-500 hover:bg-red-50 transition-opacity"
                        onClick={(e) => { e.stopPropagation(); }}
                        data-testid={`delete-kanban-${p.proposal_id}`}
                        title="Excluir proposta"
                      >
                        <Trash2 size={13} />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Excluir proposta {p.number}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta ação é permanente. A proposta "{p.title}" ({STATUS_LABEL[p.status]}) e todo o seu histórico serão removidos. Contratos vinculados permanecem, mas ficam órfãos.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`cancel-delete-${p.proposal_id}`}>Cancelar</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-red-600 hover:bg-red-700 text-white"
                          onClick={() => remove(p.proposal_id)}
                          data-testid={`confirm-delete-${p.proposal_id}`}
                        >
                          Excluir permanentemente
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              ))}
              {groups[col].length === 0 && <p className="text-xs text-muted-foreground text-center py-6">—</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
